import { Activity } from "lucide-react";

import type { ActivityItem } from "@/api";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/i18n";
import { formatRelativeTimeID } from "@/lib/format";
import { cn } from "@/lib/utils";

interface RecentActivityCardProps {
  items: ActivityItem[];
  isLoading: boolean;
  isError: boolean;
}

function severityDot(severity: ActivityItem["severity"]): string {
  switch (severity) {
    case "critical":
      return "bg-danger-600";
    case "warning":
      return "bg-warning-600";
    default:
      return "bg-ocean-400";
  }
}

/**
 * Recent activity tile. Spans 3 columns at md+ so it can sit
 * underneath the five number tiles and absorb the longer log
 * lines without truncation.
 */
export function RecentActivityCard({
  items,
  isLoading,
  isError,
}: RecentActivityCardProps) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "col-span-1 rounded-lg border border-ocean-100 bg-white p-5 transition-colors hover:border-ocean-200 tablet:col-span-2 desktop:col-span-3",
        isError && "border-warning-600/30 bg-warning-50/30"
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="label-eyebrow">
          {t("dashboard.recentActivity.label")}
        </span>
        <Activity size={16} className="text-ocean-400" />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-sm text-neutral-500">
          {t("dashboard.recentActivity.empty")}
        </div>
      ) : (
        <ul className="mt-1 divide-y divide-ocean-100/60">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-start gap-3 py-2"
            >
              <span
                className={cn(
                  "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                  severityDot(item.severity)
                )}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-ocean-800">
                  {item.action}
                </div>
                <div className="text-xs text-neutral-500">
                  {item.actor} · {formatRelativeTimeID(item.timestamp)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
